import { Suspense, lazy } from "react";
import type { ReactNode } from "react";
import clsx from "clsx";
import type { CaseView } from "./caseView";
import type { StoryMode } from "./useStoryMode";
import styles from "./LightboxSurface.module.scss";
import type { Typeface } from "./typefaces";
import SceneBoundary from "./SceneBoundary";
import { SHIPPED_ART } from "./caseArt";
import desktopPoster from "../../assets/lightbox/frames/hero-desktop.jpg";

/* UI-DESIGN: LB-14 the scene is a lazy chunk: three.js is only fetched
   when the visitor gets the live version (desktop, WebGL, motion on) */
/* UI-DESIGN: 2026-10-25 the chunk starts downloading as soon as the app
   loads, not when the surface first renders, so the scene is ready sooner */
const loadScene = () => import("./StoryScene");
/* UI-DESIGN: 2026-10-25 (Taco: still a delay) the case art downloads at the
   same moment instead of waiting ~0.7s for the case list; same request mode
   as the scene's loadImage, so it is a cache hit there */
// UI-DESIGN: inner pages must not download the home-only scene and art.
if (typeof window !== "undefined" && window.location.pathname === "/" && !new URLSearchParams(window.location.search).has("brand-system") && !window.matchMedia?.("(max-width: 768px), (prefers-reduced-motion: reduce)").matches) {
  void loadScene();
  SHIPPED_ART.forEach((url) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.fetchPriority = "low";
    img.src = url;
  });
}
const StoryScene = lazy(loadScene);

/* UI-DESIGN: the page itself is the lightbox. A fixed layer behind the
   route content paints the light; content scrolls over it.
   `data-lb-surface` lets the global HeaderBar switch to its light glass
   skin with CSS alone (no routing logic in the header). In "three" mode
   (LB-14) that layer holds the scroll-driven scene; otherwise the CSS
   backlight from LB-7 (see docs/lightbox/STORY.md). */
type LightboxSurfaceProps = {
  mode?: StoryMode;
  typeface?: Typeface;
  cases?: CaseView[];
  selectedId?: string | null;
  onChapter?: (index: number) => void;
  onPick?: (id: string) => void;
  onHover?: (id: string | null) => void;
  onUnavailable: () => void;
  className?: string;
  children: ReactNode;
};

const LightboxSurface = ({
  mode = "static",
  typeface = "typed-editorial",
  cases = [],
  selectedId = null,
  onChapter,
  onPick,
  onHover,
  onUnavailable,
  className,
  children,
}: LightboxSurfaceProps) => (
  <div className={clsx(styles.root, className)} data-lb-surface="" data-lb-mode={mode} data-lb-type={typeface}>
    <div className={styles.light} aria-hidden>
      {/* UI-DESIGN: a small still of the desktop scene covers late data/GPU work. */}
      {mode === "three" && <img className={styles.poster} data-lb-poster="" src={desktopPoster} alt="" fetchPriority="high" />}
      {mode === "three" ? (
        <SceneBoundary onUnavailable={onUnavailable}>
          <Suspense fallback={null}>
            <StoryScene typeface={typeface} cases={cases} selectedId={selectedId} onChapter={onChapter} onPick={onPick} onHover={onHover} onUnavailable={onUnavailable} />
          </Suspense>
        </SceneBoundary>
      ) : null}
      <div className={styles.grain} />
    </div>
    <div className={styles.content}>{children}</div>
  </div>
);

export default LightboxSurface;
